import { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

/**
 * Hook for managing BACnet schedule navigation and UI state
 * Handles navigation between different views and dialog states
 */
const useBacnetScheduleNavigation = (onClose) => {
  const history = useHistory();

  // UI State
  const [showAddEditSchedule, setShowAddEditSchedule] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  // Navigation actions
  const navigateToAddSchedule = useCallback(() => {
    setSelectedSchedule(null);
    setShowAddEditSchedule(true);
  }, []);

  const navigateToEditSchedule = useCallback((schedule) => {
    setSelectedSchedule(schedule);
    setShowAddEditSchedule(true);
  }, []);

  const navigateBackToList = useCallback(() => {
    setShowAddEditSchedule(false);
    setSelectedSchedule(null);
  }, []);

  const handleBackClicked = useCallback(() => {
    if (showAddEditSchedule) {
      navigateBackToList();
    } else {
      onClose ? onClose() : history.push('/site');
    }
  }, [showAddEditSchedule, navigateBackToList, onClose, history]);

  // Schedule management actions
  const handleAddSchedule = useCallback(() => {
    navigateToAddSchedule();
  }, [navigateToAddSchedule]);

  const handleEditSchedule = useCallback((schedule) => {
    navigateToEditSchedule(schedule);
  }, [navigateToEditSchedule]);


  const handleScheduleCreated = useCallback((newSchedule) => {
    // Navigate back to list after successful creation
    navigateBackToList();
    return newSchedule;
  }, [navigateBackToList]);

  const handleScheduleUpdated = useCallback((updatedSchedule) => {
    // Navigate back to list after successful update
    navigateBackToList();
    return updatedSchedule;
  }, [navigateBackToList]);

  // Reset all navigation state
  const resetNavigation = useCallback(() => {
    setShowAddEditSchedule(false);
    setSelectedSchedule(null);
  }, []);

  // Get current view state
  const getCurrentView = useCallback(() => {
    if (showAddEditSchedule) {
      return selectedSchedule ? 'edit' : 'add';
    }
    return 'list';
  }, [showAddEditSchedule, selectedSchedule]);

  // Get header title based on current view
  const getHeaderTitle = useCallback(() => {
    const currentView = getCurrentView();
    switch (currentView) {
      case 'add':
        return 'Add Schedule';
      case 'edit':
        return 'Edit Schedule';
      case 'list':
      default:
        return 'Schedules';
    }
  }, [getCurrentView]);

  return {
    // UI State
    showAddEditSchedule,
    selectedSchedule,

    // Navigation actions
    navigateToAddSchedule,
    navigateToEditSchedule,
    navigateBackToList,
    handleBackClicked,

    // Schedule actions
    handleAddSchedule,
    handleEditSchedule,
    handleScheduleCreated,
    handleScheduleUpdated,
    
    // Utilities
    resetNavigation,
    getCurrentView,
    getHeaderTitle,
    
    // Manual state setters (for external control)
    setShowAddEditSchedule,
    setSelectedSchedule,
  };
};

export default useBacnetScheduleNavigation;
